import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Card,
  DatePicker,
  InputRef,
  Spin,
  Table,
  TableColumnType,
  Switch,
  Button,
  Tag,
  Tooltip,
  notification,
  Space,
  TableColumnsType,
} from "antd"; // Import Table from antd
import { SubmitHandler, useForm } from "react-hook-form";
// utils
import {
  CannyDevice,
  CannyDeviceIndex,
  CannyIForm,
  FirmwareBase,
  StringSelectObject,
} from "../../../utils/interfaces";
import { BASE_URL } from "../../../utils/constants";
import { FilterDropdownProps, SorterResult, TablePaginationConfig } from "antd/es/table/interface";
import Search from "antd/es/input/Search";
import {
  CalendarOutlined,
  CheckOutlined,
  CloseOutlined,
  CommentOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import dayjs, { Dayjs } from "dayjs";
import CommentsComponent from "../../../Components/CommentsComponent";
import DeleteModal from "../../GlobalComponents/Modals/DeleteModal";
import AddCannyModal from "../../../Components/CannyListPageComp/Modals/AddCannyModal";
import EditCannyModal from "../../../Components/CannyListPageComp/Modals/EditCannyModal";
import styles from '../../../global.module.css'
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useHasAction } from "../../../hooks/useHasAction";
// components

axios.defaults.withCredentials = true;

// Lazily-loaded pages: /get_canny_devices?offset=&limit= returns one batch
// plus the total row count (same on every page, used to know when there's
// nothing left to fetch) - mirrors VehicleListView's VehiclePage.
type CannyDevicePage = {
  items: CannyDevice[];
  total: number;
};

const CANNY_PAGE_SIZE = 1000;

const CannyListView: React.FC = () => {
  const canDownloadCannyDevicesExcel: boolean = useHasAction("canny_manager_download_excel");
  const canAddCannyDevices: boolean = useHasAction("canny_manager_add_device");
  const canEditCannyDevices: boolean = useHasAction("canny_manager_edit_device");
  const canDeleteCannyDevices: boolean = useHasAction("canny_manager_delete_device");

  const [hardwareVersions, setHardwareVersions] = useState<StringSelectObject[]>([]);
  const [selectedCanny, setSelectedCanny] = useState<CannyDevice | null>(null);
  const [firmwareVersions, setFirmwareVersions] = useState<FirmwareBase[]>([]);
  const [qcOptions] = useState<StringSelectObject[]>([
    { value: true, label: "True" },
    { value: false, label: "False" },
  ]);
  const [cannyStates, setCannyStates] = useState<StringSelectObject[]>([]);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const [searchText, setSearchText] = useState<string>("");
  const [searchedColumn, setSearchedColumn] = useState<string>("");
  const searchInput = useRef<InputRef>(null);

  const [showCommentModal, setShowCommentModal] = useState<boolean>(false);
  const [selectedCannyId, setSelectedCannyId] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingDownload, setLoadingDownload] = useState<boolean>(false);
  const [loadingButtons, setLoadingButtons] = useState<boolean>(false);
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});

  const [api, contextHolder] = notification.useNotification();

  const [tableScrollY, setTableScrollY] = useState(() => window.innerHeight - 255);
  useEffect(() => {
    const onResize = () => setTableScrollY(window.innerHeight - 255);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const latestFirmwareQuery = useQuery({
    queryKey: ["get_latest_firmware"],
    queryFn: async () => {
        const res = await axios.get(`${BASE_URL}/get_latest_firmware`);
        // console.log("Latest firmware data:", res.data);
        if(res.data.content === 'null'){
          return null;
        } else {
          return res.data.content as FirmwareBase;
        }
    },
  });

  const cannyDevicesQuery = useInfiniteQuery({
    queryKey: ["cannyDeviceList"],
    queryFn: async ({ pageParam }) => {
      const res = await axios.get<{ success: boolean; content: CannyDevicePage }>(
        `${BASE_URL}get_canny_devices`,
        { params: { offset: pageParam, limit: CANNY_PAGE_SIZE } }
      );
      return res.data.content;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((sum, page) => sum + page.items.length, 0);
      return loadedCount < lastPage.total ? loadedCount : undefined;
    },
  });

  const cannyDevices = useMemo(
    () => cannyDevicesQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [cannyDevicesQuery.data]
  );
  const totalCannyDeviceCount = cannyDevicesQuery.data?.pages[0]?.total ?? null;
  const stillLoadingMoreCannyDevices = cannyDevicesQuery.hasNextPage || cannyDevicesQuery.isFetchingNextPage;

  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const [activeSorters, setActiveSorters] = useState<SorterResult<CannyDevice>[]>([]);
  const hasActiveFilterOrSort =
    Object.values(activeFilters).some((value) => Array.isArray(value) && value.length > 0) ||
    activeSorters.some((sorter) => !!sorter.order);

  const isForceLoadingForFilterOrSort = hasActiveFilterOrSort && stillLoadingMoreCannyDevices;

  const handleTableChange = (
    _pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: SorterResult<CannyDevice> | SorterResult<CannyDevice>[]
  ) => {
    setActiveFilters(filters);
    setActiveSorters(Array.isArray(sorter) ? sorter : [sorter]);
  };

  useEffect(() => {
    if (!hasActiveFilterOrSort) return;
    if (!cannyDevicesQuery.hasNextPage || cannyDevicesQuery.isFetchingNextPage) return;
    cannyDevicesQuery.fetchNextPage();
  }, [hasActiveFilterOrSort, cannyDevicesQuery.hasNextPage, cannyDevicesQuery.isFetchingNextPage]);// eslint-disable-line react-hooks/exhaustive-deps

  const handleTableScroll = (e: React.UIEvent<HTMLElement>) => {
    if (hasActiveFilterOrSort) return; // already being force-loaded above
    const target = e.currentTarget;
    const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 200;
    if (nearBottom && cannyDevicesQuery.hasNextPage && !cannyDevicesQuery.isFetchingNextPage) {
      cannyDevicesQuery.fetchNextPage();
    }
  };

  const COLUMN_FILTER_COLORS: Record<string, string> = {
    created_at: "#52c41a",
    hardware_version: "#1677ff",
    mac_address: "#722ed1",
    pin: "#13c2c2",
    serial_no: "#fa541c",
    status: "#faad14",
  };

  const COLUMN_FILTER_LABELS: Record<string, string> = {
    created_at: "Created at",
    hardware_version: "Hardware Version",
    mac_address: "MAC Address",
    pin: "PIN",
    serial_no: "Serial No.",
    status: "Status",
  };

  const CREATED_AT_RANGE_SEPARATOR = "|";

  const parseCreatedAtRangeKey = (raw: unknown): [Dayjs, Dayjs] | null => {
    if (typeof raw !== "string" || !raw.includes(CREATED_AT_RANGE_SEPARATOR)) return null;
    const [start, end] = raw.split(CREATED_AT_RANGE_SEPARATOR);
    const startDate = dayjs(start);
    const endDate = dayjs(end);
    return startDate.isValid() && endDate.isValid() ? [startDate, endDate] : null;
  };

  const renderColumnTitle = (key: string, label: string) => (
    <Space size={6}>
      <span>{label}</span>
      {Array.isArray(activeFilters[key]) && activeFilters[key].length > 0 && (
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            display: "inline-block",
            background: COLUMN_FILTER_COLORS[key],
          }}
        />
      )}
    </Space>
  );

  // Drops one value from one column's filter, leaving any other selected
  // values on that same column untouched.
  const removeFilterValue = (key: string, value: any) => {
    setActiveFilters((prev) => {
      const current = prev[key];
      if (!Array.isArray(current)) return prev;
      const next = current.filter((v) => v !== value);
      const updated = { ...prev };
      if (next.length > 0) {
        updated[key] = next;
      } else {
        delete updated[key];
      }
      return updated;
    });
    if (searchedColumn === key) {
      setSearchText("");
      setSearchedColumn("");
    }
  };

  // Every currently applied column filter, as one flat list of removable
  // chips - shown in the card title (see the filter-chips row below).
  const filterChips = useMemo(() => {
    const chips: { key: string; label: string; color: string; onRemove: () => void }[] = [];
    Object.entries(activeFilters).forEach(([key, values]) => {
      if (!Array.isArray(values)) return;
      values.forEach((value) => {
        const range = key === "created_at" ? parseCreatedAtRangeKey(value) : null;
        const displayValue = range ? `${range[0].format("YYYY-MM-DD")} → ${range[1].format("YYYY-MM-DD")}` : value;
        chips.push({
          key: `${key}-${value}`,
          label: `${COLUMN_FILTER_LABELS[key] ?? key}: ${displayValue}`,
          color: COLUMN_FILTER_COLORS[key] ?? "default",
          onRemove: () => removeFilterValue(key, value),
        });
      });
    });
    return chips;
  }, [activeFilters, searchedColumn]);// eslint-disable-line react-hooks/exhaustive-deps

  const clearAllFilters = () => {
    setActiveFilters({});
    setSearchText("");
    setSearchedColumn("");
  };

  const openCommentModal = (canny: CannyDevice) => {
    setSelectedCanny(canny);
    setSelectedCannyId(canny.id);
    setShowCommentModal(true);
  };

  const closeCommentModal = () => {
    setShowCommentModal(false);
  };

  const {
    control: cannyAddControl,
    handleSubmit: cannyAddSubmit,
    formState: { errors: cannyAddErrors , isValid: cannyAddIsValid },
    reset: cannyAddReset,
  } = useForm<CannyIForm>({mode: "onChange"});

  const {
    control: cannyEditControl,
    handleSubmit: cannyEditSubmit,
    formState: { errors: cannyEditErrors , isValid: cannyEditIsValid, isDirty: cannyEditIsDirty },
    reset: cannyEditReset,
  } = useForm<CannyDevice>({mode: "onChange"});

  const onSubmit: SubmitHandler<CannyIForm> = (data) => {
    setLoadingButtons(true);
    console.log(data);
    axios
      .post(`${BASE_URL}generate_canny`, {
        no_items_in_batch: data.items_in_batch,
        // "firmware_version": data.firmware_version?.name,
        hardware_version: data.hardware_version,
      })
      .then((response) => {
        console.log(response.data);
        cannyDevicesQuery.refetch();
        toggleAddModal();
        api.success({
          title: "Canny Devices Added",
          description: `The Canny devices were added successfully.`,
          placement: "bottomRight",
          showProgress: true
        });
      })
      .catch((error) => {
        console.error("Error adding Canny Devices:", error);
        api.error({
          title: "Error Adding Canny Devices",
          description: `There was an error adding the Canny devices: ${error.message}`,
          placement: "bottomRight",
          showProgress: true
        });
      })
      // Was only reset on success before - a failed request left the Save
      // button stuck disabled/spinning forever.
      .finally(() => setLoadingButtons(false));
  };

  const updateCanny: SubmitHandler<CannyDevice> = (data) => {
    setLoadingButtons(true);
    console.log("UPDATE API CALL");
    console.log(data);
    axios
      .post(`${BASE_URL}canny_update_device`, {
        serial_no: data.serial_no,
        latest_firmware_id: data?.latest_firmware_id,
        hardware_version: data?.hardware_version,
        qc_pass: data?.qc_pass,
        status: data?.status,
      })
      .then((response) => {
        cannyDevicesQuery.refetch();
        toggleEditModal();
        api.success({
          title: "Canny Device Updated",
          description: `The Canny device with serial number ${data.serial_no} was updated successfully.`,
          placement: "bottomRight",
          showProgress: true
        });
      })
      .catch((error) => {
        console.error("Error fetching Users:", error);
        api.error({
          title: "Error Updating Canny Device",
          description: `There was an error updating the Canny device with serial number ${data.serial_no} : ${error.message}`,
          placement: "bottomRight",
          showProgress: true
        });
      })
      // Was only reset on success before - a failed request left the Save
      // button stuck disabled/spinning forever.
      .finally(() => setLoadingButtons(false));
  };

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: CannyDeviceIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void, close: () => void) => {
    clearFilters();
    setSearchText("");

    close();
  };

  const getColumnSearchProps = (
    dataIndex: CannyDeviceIndex,
      displayMapper?: (record: CannyDevice) => string
  ): TableColumnType<CannyDevice> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Search
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onSearch={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "flex" }}
          allowClear={{
            clearIcon: (
              <CloseOutlined
                onClick={() => clearFilters && handleReset(clearFilters, close)}
              />
            ),
          }}
        />
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <>
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      </>
    ),
    // Controlled so the filter-chips row (see filterChips) can clear this
    // column's search from outside the column's own dropdown.
    filteredValue: activeFilters[dataIndex as string] ?? null,
    onFilter: (value, record) => {
      const fieldValue = displayMapper
        ? displayMapper(record)
        : // @ts-ignore
          record[dataIndex]?.toString();

      return fieldValue
        ?.toLowerCase()
        .includes((value as string).toLowerCase()) ?? false;
    },

    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },

    render: (_text, record) => {
      const displayValue = displayMapper
        ? displayMapper(record)
        : // @ts-ignore
          _text?.toString();

      return searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={displayValue || ""}
        />
      ) : (
        displayValue
      );
    },
  });

  const getCreatedAtFilterProps = (): TableColumnType<CannyDevice> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
      const range = parseCreatedAtRangeKey(selectedKeys[0]);
      return (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <DatePicker.RangePicker
            value={range}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                const startKey = dates[0].startOf("day").toISOString();
                const endKey = dates[1].endOf("day").toISOString();
                setSelectedKeys([`${startKey}${CREATED_AT_RANGE_SEPARATOR}${endKey}`]);
              } else {
                setSelectedKeys([]);
              }
            }}
            style={{ marginBottom: 8, display: "flex" }}
          />
          <Space>
            <Button type="primary" size="small" style={{ width: 90 }} onClick={() => confirm()}>
              Filter
            </Button>
            <Button
              size="small"
              style={{ width: 90 }}
              onClick={() => {
                clearFilters?.();
                confirm();
              }}
            >
              Reset
            </Button>
          </Space>
        </div>
      );
    },
    filterIcon: (filtered: boolean) => (
      <CalendarOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    filteredValue: activeFilters.created_at ?? null,
    onFilter: (value, record) => {
      const range = parseCreatedAtRangeKey(value);
      if (!range || !record.created_at) return false;
      const created = dayjs(record.created_at);
      return created.isValid() && !created.isBefore(range[0]) && !created.isAfter(range[1]);
    },
  });

  const toggleEditModal = () => {
    setShowEditModal(!showEditModal);
  };

  const toggleAddModal = () => {
    setShowAddModal(!showAddModal);
    cannyAddReset();
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      try {
        // cannyDevicesQuery loads its own first page independently.
        await Promise.all([
          updateFirmwareVersions(),
          updateHardwareVerions(),
          updateCannyStates(),
        ]);
      } catch (error) {
        console.error("Failed to load data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleAllowRecordChange = (checked: boolean, serial_no: string) => {
    setLoadingStates((prev) => ({ ...prev, [serial_no]: true }));
    axios
      .post(`${BASE_URL}canny_update_device`, {
        serial_no: serial_no,
        allow_record: checked,
      })
      .then((response) => {
        console.log("Update successful:", response.data);
        cannyDevicesQuery.refetch();
        setLoadingStates((prev) => ({ ...prev, [serial_no]: false }));
        api.success({
          title: "Allow Record Updated",
          description: `The allow record status for serial number ${serial_no} was updated successfully.`,
          placement: "bottomRight",
          showProgress: true
        });
      })
      .catch((error) => {
        setLoadingStates((prev) => ({ ...prev, [serial_no]: false }));
        console.error("Error updating allow_record:", error);
        api.error({
          title: "Error Updating Allow Record",
          description: `There was an error updating the allow record status for serial number ${serial_no} : ${error.message}`,
          placement: "bottomRight",
          showProgress: true
        });
      });
  };

  // Columns definition for Ant Design Table
  const columns: TableColumnsType<CannyDevice> = [
    {
      title: renderColumnTitle("created_at", "Created at"),
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a: CannyDevice, b: CannyDevice) =>
        a.created_at.localeCompare(b.created_at),
      ...getCreatedAtFilterProps(),
      width: 200,
    },
    {
      title: "Firmware Version",
      dataIndex: "firmware_base_id",
      key: "firmware_base_id",
      render: (_text: any, record: CannyDevice) => {
        const firmware = firmwareVersions.find(
          (fw) => fw.id === record.firmware_base_id
        );
        if (!record.firmware_base_id) {
          return <Tag color="red">N/A</Tag>;
        }
        const isUpToDate = record.latest_firmware_id
          ? record.firmware_base_id === record.latest_firmware_id
          : record.firmware_base_id === latestFirmwareQuery.data?.id;
        return (
          <Tag color={isUpToDate ? "success" : "gold"}>
            {firmware ? firmware.version_name : "N/A"}
          </Tag>
        );
      },
      width: 80
    },
    {
      title: "Latest Firmware",
      dataIndex: "latest_firmware_id",
      key: "latest_firmware_id",
      render: (_text: any, record: CannyDevice) => {
        const firmware = firmwareVersions.find(
          (fw) => fw.id === record.latest_firmware_id
        );
        const hasLatest = record.latest_firmware_id || latestFirmwareQuery.data?.id;
        return (
          <Tag color={hasLatest ? "success" : "red"}>
            {record.latest_firmware_id ? firmware?.version_name :
            (latestFirmwareQuery.data ? 'Latest Firmware' : "N/A")}
          </Tag>
        );
      },
      width: 100
    },
    {
      title: renderColumnTitle("hardware_version", "Hardware Version"),
      dataIndex: "hardware_version",
      key: "hardware_version",
      ...getColumnSearchProps("hardware_version"),
      width: 100
    },
    {
      title: renderColumnTitle("mac_address", "MAC Address"),
      dataIndex: "mac_address",
      key: "mac_address",
      ...getColumnSearchProps("mac_address"),
      width: 130,
      fixed: "left" as const,
    },
    {
      title: renderColumnTitle("pin", "PIN"),
      dataIndex: "pin",
      key: "pin",
      ...getColumnSearchProps("pin"),
      width: 60
    },
    {
      title: "QC",
      dataIndex: "qc_pass",
      key: "qc_pass",
      render: (value: boolean) =>
        value
          ? <CheckOutlined style={{ color: "green" }} />
          : <CloseOutlined style={{ color: "red" }} />,
      width: 50
    },
    {
      title: "Allow Record",
      dataIndex: "allow_record",
      key: "allow_record",
      render: (value: boolean, record: CannyDevice) => (
        <Switch
          loading={loadingStates[record.serial_no] || false}
          disabled={!canEditCannyDevices}
          checked={value}
          checkedChildren="Yes"
          unCheckedChildren="No"
          onChange={(checked) =>
            handleAllowRecordChange(checked, record.serial_no)
          }
        />
      ),
      width: 60
    },
    {
      title: renderColumnTitle("serial_no", "Serial No."),
      dataIndex: "serial_no",
      key: "serial_no",
      ...getColumnSearchProps("serial_no"),
      width: 100,
      fixed: "left" as const,
    },
    {
      title: renderColumnTitle("status", "Status"),
      dataIndex: "status",
      key: "status",
      filters: cannyStates.map((state) => ({ text: state.label, value: state.value })),
      filteredValue: activeFilters.status ?? null,
      onFilter: (value: boolean | React.Key, record: CannyDevice) => record.status === value,
      width: 100,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_text: any, record: CannyDevice) => (
        <Space wrap>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            disabled={!canEditCannyDevices}
          >
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            disabled={!canDeleteCannyDevices}
            onClick={() => handleDeleteCanny(record)}
          >
            Delete
          </Button>
          <Tooltip title="View Comments">
            <Button
              icon={<CommentOutlined />}
              onClick={() => openCommentModal(record)}
            />
          </Tooltip>
        </Space>
      ),
      width: 200,
      fixed: "right" as const,
    }
  ];

  const updateHardwareVerions = () => {
    return axios
      .get(`${BASE_URL}canny_get_hw`)
      .then((response) => {
        // Map the array of strings to an array of objects with 'value' and 'label' properties
        const formattedVersions = response.data.content.map((version: any) => ({
          value: version,
          label: version,
        }));
        // Set the state with the formatted array
        setHardwareVersions(formattedVersions);
      })
      .catch((error) => {
        console.error("Error fetching Canny Devices:", error);
      });
  };

  const updateCannyStates = () => {
    return axios
      .get(`${BASE_URL}get_canny_states`)
      .then((response) => {
        // Map the array of strings to an array of objects with 'value' and 'label' properties
        const formattedVersions = response.data.content.map((version: any) => ({
          value: version,
          label: version,
        }));
        // Set the state with the formatted array
        setCannyStates(formattedVersions);
      })
      .catch((error) => {
        console.error("Error fetching Canny Devices:", error);
      });
  };

  const updateFirmwareVersions = () => {
    return axios
      .get(`${BASE_URL}get_firmware_bases`)
      .then((response) => {
        const fw_versions = response.data.content;
        setFirmwareVersions(fw_versions);
        // if (fw_versions.length > 0) {
        //   console.log(fw_versions);
        // }
      })
      .catch((error) => {
        console.error("Error fetching Canny Devices:", error);
      });
  };

  const downloadExcel = () => {
    setLoadingDownload(true);
    axios({
      url: `${BASE_URL}/get_printable_devices`,
      method: "GET",
      responseType: "blob",
    }).then((response) => {
      // create file link in browser's memory
      const href = URL.createObjectURL(response.data);

      // create "a" HTML element with href to file & click
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", "labels.xlsx"); //or any other extension
      document.body.appendChild(link);
      link.click();

      // clean up "a" element & remove ObjectURL
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
      cannyDevicesQuery.refetch();
      setLoadingDownload(false);
      api.success({
        title: "Excel Downloaded",
        description: "The Excel file with Canny devices has been downloaded successfully.",
        placement: "bottomRight",
        showProgress: true
      });
    }).catch((error) => {
      console.error("Error downloading Excel:", error);
      api.error({
        title: "Error Downloading Excel",
        description: `There was an error downloading the Excel file: ${error.message}`,
        placement: "bottomRight",
        showProgress: true
      });
    });

  };

  const handleEdit = (cannyDevice: CannyDevice) => {
    cannyEditReset(cannyDevice);
    setSelectedCanny(cannyDevice);
    updateHardwareVerions();
    toggleEditModal();
  };

  const confirmDeleteCanny = (cannyID: number) => {
    console.log("Delete Canny with ID:", cannyID);
    axios
      .post(`${BASE_URL}delete_canny/${cannyID}`)
      .then((response) => {
        cannyDevicesQuery.refetch();
        api.success({
          title: "Canny Device Deleted",
          description: `The Canny device with ID ${cannyID} was deleted successfully.`,
          placement: "bottomRight",
          showProgress: true
        });
      })
      .catch((error) => {
        console.error("Error fetching Canny device:", error);
        api.error({
          title: "Error Deleting Canny Device",
          description: `There was an error deleting the Canny device with ID ${cannyID} : ${error.message}`,
          placement: "bottomRight",
          showProgress: true
        });
      });
    setShowDeleteModal(false);
  };

  const handleDeleteCanny = (canny: CannyDevice) => {
    setSelectedCanny(canny);
    setShowDeleteModal(true);
  };

  // @ts-ignore
  return (
    <div>
      {contextHolder}
      <div>
        <Card
          className={styles.globalCard}
          title={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <span style={{ flexShrink: 0 }}>Canny Device Management</span>
              {filterChips.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 6, overflowX: "auto", flex: 1, padding: "2px 0" }}>
                    {filterChips.map((chip) => (
                      <Tag
                        key={chip.key}
                        color={chip.color}
                        closable
                        onClose={(e) => {
                          e.preventDefault();
                          chip.onRemove();
                        }}
                        style={{ flexShrink: 0 }}
                      >
                        {chip.label}
                      </Tag>
                    ))}
                  </div>
                  <Button size="small" type="link" onClick={clearAllFilters} style={{ flexShrink: 0 }}>
                    Clear all
                  </Button>
                </div>
              )}
              <span style={{ display: 'flex', gap: '5px', flexShrink: 0, marginLeft: filterChips.length > 0 ? 0 : "auto" }}>
                <Button icon={<PlusOutlined />} onClick={toggleAddModal} style={{fontSize:12}} disabled={!canAddCannyDevices} type="primary">Add Canny Devices</Button>
                <Button icon={<DownloadOutlined />} onClick={downloadExcel} style={{fontSize:12}} loading={loadingDownload} disabled={!canDownloadCannyDevicesExcel} type="primary">Download Excel</Button>
              </span>
            </div>
          }
        >
          <Table
            columns={columns}
            dataSource={cannyDevices}
            loading={loading || cannyDevicesQuery.isLoading || isForceLoadingForFilterOrSort}
            scroll={{x : 'max-content', y: tableScrollY}}
            virtual
            pagination={false}
            rowKey="id"
            className='horizontally-synced-table'
            onChange={handleTableChange}
            onScroll={handleTableScroll}
            footer={
              !hasActiveFilterOrSort && cannyDevicesQuery.isFetchingNextPage
                ? () => (
                    <div style={{ textAlign: "center" }}>
                      <Spin size="small" /> Loading more Canny devices ({cannyDevices.length}
                      {totalCannyDeviceCount !== null ? ` / ${totalCannyDeviceCount}` : ""})...
                    </div>
                  )
                : undefined
            }
          />
        </Card>
      </div>
      <CommentsComponent
        showModal={showCommentModal}
        onClose={closeCommentModal}
        commentTarget={"CANNY"}
        targetId={selectedCannyId}
        targetObject={selectedCanny}
        api={api}
      />
      <AddCannyModal
        showAddModal={showAddModal}
        toggleAddModal={toggleAddModal}
        loadingButtons={loadingButtons}
        handleSubmit={cannyAddSubmit}
        errors={cannyAddErrors}
        onSubmit={onSubmit}
        control={cannyAddControl}
        isValid={cannyAddIsValid}
        hardwareVersions={hardwareVersions}
      />
      <EditCannyModal
        showEditModal={showEditModal}
        toggleEditModal={toggleEditModal}
        loadingButtons={loadingButtons}
        firmwareVersions={firmwareVersions.filter(fw => fw.status === 'RELEASED')}
        selectedCanny={selectedCanny}
        hardwareVersions={hardwareVersions}
        qcOptions={qcOptions}
        cannyStates={cannyStates}
        updateCanny={updateCanny}
        cannyEditSubmit={cannyEditSubmit}
        control={cannyEditControl}
        errors={cannyEditErrors}
        isValid={cannyEditIsValid}
        isDirty={cannyEditIsDirty}
      />
      <DeleteModal
        showModal={showDeleteModal}
        onDelete={() => confirmDeleteCanny(selectedCanny!.id)}
        onClose={() => setShowDeleteModal(false)}
      />
    </div>
  );
};

export default CannyListView;
